/**
 * "2026 UI" gating.
 *
 * Whether the app renders the new 2026 ("2.0") versions of pages is driven by
 * the `cip2.0-ui` beta feature, enabled for a session only when BOTH the
 * current user AND the selected clinic are enrolled (the intersection). See
 * {@link usesCip20Ui} for the underlying check.
 *
 * - ALWAYS resolves to `false` in Compassus mode, regardless of the beta
 *   features. "Compassus mode" means ANY of (see {@link isCompassusMode}):
 *     - the Compassus subdomain, OR
 *     - the selected clinic's `clinic_type === "compassus"`, OR
 *     - the selected clinic's `config.isHomeHealthRPM === true`.
 *
 * React components should prefer the reactive `useUse2026UI` hook. This module
 * exposes an imperative reader ({@link isUse2026UIEnabled}) for non-React
 * callers (e.g. default landing path resolution).
 */

import store from "../redux";
import { usesCip20Ui } from "./betaFeatures";
import { isCompassusSubdomain, isInternalLaunchOverride } from "./subdomain";

/**
 * Pure "is this Compassus mode?" test from explicit inputs. Kept dependency-free
 * (aside from the synchronous subdomain check) so it can be reused by the React
 * hook with live Redux values as well as by the imperative reader below.
 */
export const isCompassusMode = (args?: {
  clinicType?: string | null;
  isHomeHealthRPM?: boolean | null;
}): boolean => {
  if (isCompassusSubdomain()) {
    return true;
  }
  if (args?.clinicType === "compassus") {
    return true;
  }
  return args?.isHomeHealthRPM === true;
};

/**
 * Imperative Compassus-mode check that reads the current Redux state. Use this
 * outside React (one-off reads). React components should prefer the reactive
 * `useUse2026UI` hook.
 */
const isCompassusModeFromStore = (): boolean => {
  try {
    const state = store.getState();
    return isCompassusMode({
      clinicType: state.clinic?.userClinic?.clinic_type,
      isHomeHealthRPM: state.clinic?.userClinic?.config?.isHomeHealthRPM,
    });
  } catch {
    // If the store isn't ready, fall back to the always-available subdomain.
    return isCompassusSubdomain();
  }
};

/** The current user's email from the store, or ``null`` if unavailable. */
const currentUserEmail = (): string | null => {
  try {
    return store.getState().auth?.user?.email ?? null;
  } catch {
    return null;
  }
};

/**
 * Whether this session's clinic context has resolved: a clinic is selected
 * (persisted, so it rehydrates fast on repeat logins) or the clinic beta
 * features have loaded (the no-clinic case). The internal launch override waits
 * for this so it can't fire before the Compassus kill-switch is authoritative.
 */
const isClinicContextResolvedFromStore = (): boolean => {
  try {
    const state = store.getState();
    return (
      !!state.clinic?.userClinic ||
      state.auth?.clinicBetaFeaturesLoaded === true
    );
  } catch {
    return false;
  }
};

/**
 * Read the effective setting: the `cip2.0-ui` user∩clinic intersection OR the
 * internal launch override (internal Circadia employee on a beta/local host,
 * applied only once the clinic context has resolved), forced to `false`
 * whenever the app is in Compassus mode.
 */
export const isUse2026UIEnabled = (): boolean => {
  // Compassus mode never gets the 2026 UI, whatever the beta features say.
  if (isCompassusModeFromStore()) {
    return false;
  }
  if (usesCip20Ui()) {
    return true;
  }
  return (
    isClinicContextResolvedFromStore() &&
    isInternalLaunchOverride(currentUserEmail())
  );
};
